import * as PIXI from 'pixi.js';
import { gsap } from "gsap";
import { shallow } from 'zustand/shallow';

import { FactionType, UnitState, HexPoint } from '@/core/types/battle.types';
import { useBattleStore } from '@/store/useBattleStore';
import { IDLE_ANIMATION_DURATION } from '@/core/constants/battle.constants';
import { getUnitTotalHp } from '@/core/utils/battle.utils';


const MOVEMENT_ANIMATION_DURATION_MULTIPLIER = 0.2;

export class UnitStack extends PIXI.Container {
    public readonly unitId: string;
    public readonly faction: FactionType;
    public readonly config: any;
    public q: number;
    public r: number;

    private visual: PIXI.Container;
    public view: PIXI.Sprite;
    private badgeContainer: PIXI.Container;
    private countText: PIXI.BitmapText;
    private shadowView: PIXI.Sprite;

    private _state: UnitState = UnitState.Idle;
    private unsubStore: () => void;

    private isHighlighted: boolean = false; // Контролируется BattleManager
    private readonly FORWARD_PX = 22;
    private readonly WINDUP_MS = 170;
    private readonly STRIKE_MS = 65;
    private readonly RECOVER_MS = 240;
    private readonly KNOCKBACK_PX = 18;

    private hpBar!: PIXI.Graphics;
    private hpBarBg!: PIXI.Graphics;

    private HP_BAR_WIDTH = 80;
    private readonly HP_BAR_HEIGHT = 8;

    private impactPromise: Promise<void> | null = null;
    private resolveImpact: (() => void) | null = null;

    constructor(texture: PIXI.Texture, config: any, id: string, faction: FactionType, q: number, r: number, x: number, y: number) {
        super();

        this.x = x;
        this.y = y;
        this.zIndex = y;
        
        this.unitId = id;
        this.faction = faction;
        this.config = config;
        this.q = q;
        this.r = r;
        this.scale = 0.75;

        // ВАЖЛИВО: Вмикаємо сортування дітей, щоб текст точно був поверх спрайта
        this.sortableChildren = true;

        // --- VISUAL CONTAINER: wrapper for breathing offset ---
        this.visual = new PIXI.Container();
        this.visual.zIndex = 0;
        this.addChild(this.visual);

        // --- Store Subscription ---
        this.unsubStore = useBattleStore.subscribe(
            (state) => ({
                unit: state.units.get(this.unitId),
                selectedSkillId: state.selectedSkillId,
            }),
            ({ unit }) => {
                if (unit) this.refreshVisuals(unit);
                // this.eventMode = selectedSkillId ? 'none' : 'static';
            },
            { equalityFn: shallow }
        );

        const ax = config.anchor?.x ?? 0.5;
        const ay = config.anchor?.y ?? 0.7;

        this.shadowView = new PIXI.Sprite(PIXI.Assets.get('unit_shadow')); 
        this.shadowView.anchor.set(ax, ay);
        this.shadowView.y = 32; // Зсув вниз, під ноги (можливо доведеться підігнати під спрайти)
        this.shadowView.alpha = 0.4; // Базова прозорість
        this.shadowView.zIndex = -1; // Малюємо завжди ПІД `this.visual` (zIndex = 0)
        this.addChild(this.shadowView);

        
        this.view = new PIXI.Sprite(texture);
        
        this.view.anchor.set(ax, ay);
        this.view.zIndex = 0; // Шар 0 (Знизу)

        this.eventMode = 'none';

        const isEnemy = this.faction === FactionType.Enemy

        if (isEnemy) {
            this.view.scale.x = -1; 
        }

        this.visual.addChild(this.view);
        

        // Initial render
        const state = useBattleStore.getState();
        const unitData = state.units.get(this.unitId);

        this.countText = new PIXI.BitmapText({
            text: unitData?.count,
            style: { fontFamily: 'KyivTypeBitmap', fontSize: 36 },
            tint: 0xe0d7c6,
        });

        this.badgeContainer = this.createUnitBadge();
        this.badgeContainer.zIndex = 10; // Завжди зверху
        const badgePosX = config.badge?.x || 50;
        const badgePosY = config.badge?.y || -200;
        const finalX = this.faction === FactionType.Enemy ? -badgePosX : badgePosX;
        this.badgeContainer.position.set(finalX, badgePosY);
        this.addChild(this.badgeContainer);

        this.refreshVisuals(unitData);
    }

    private createUnitBadge() {
        const container = new PIXI.Container();
        const isEnemy = this.faction === FactionType.Enemy;
        const badgeAsset = isEnemy ? 'badge_enemy' : 'badge';
        
        const badge = new PIXI.NineSliceSprite({
            texture: PIXI.Assets.get(badgeAsset),
            leftWidth: 15, 
            topHeight: 6,
            rightWidth: 15,
            bottomHeight: 6,
        });

        const badgeWidth = Math.max(42, this.countText.width + 42);
        const badgeHeight = 40;
        badge.width = badgeWidth;
        badge.height = badgeHeight;

        this.countText.anchor.set(0.5, 0.48);
        this.countText.position.set(badgeWidth / 2, badgeHeight / 2);

        container.addChild(badge);
        container.addChild(this.countText);

        // HP bar
        this.hpBarBg = new PIXI.Graphics();
        this.hpBarBg.roundRect(0, 0, this.HP_BAR_WIDTH, this.HP_BAR_HEIGHT, 2);
        this.hpBarBg.fill(0x1a1a1a);

        this.hpBar = new PIXI.Graphics();

        const hpContainer = new PIXI.Container();
        hpContainer.addChild(this.hpBarBg);
        hpContainer.addChild(this.hpBar);

        hpContainer.position.set(
            isEnemy ? badgeWidth + 10 : -10 - this.HP_BAR_WIDTH,
            (badgeHeight - this.HP_BAR_HEIGHT) / 2
        );

        container.addChild(hpContainer);
        container.pivot.set(badgeWidth / 2, badgeHeight / 2);
        container.alpha = 0.75;

        return container;
    }

    private updateHpBar(unitData: any): void {
        const pct = Math.max(0, Math.min(1, getUnitTotalHp(unitData) / unitData.maxUnitHp));
        const fillW = Math.max(1, this.HP_BAR_WIDTH * pct);
        
        const isEnemy = this.faction === FactionType.Enemy;
        const color = isEnemy ? 0x8a1a1a : 0x2a6a1a;
        // const color = pct > 0.5 ? 0x4a8a2a : pct > 0.25 ? 0xb8862a : 0x8a1a1a;

        this.hpBar.clear();
        this.hpBar.roundRect(0, 0, fillW, this.HP_BAR_HEIGHT, 2);
        this.hpBar.fill(color);
    }

    public refreshVisuals(unitData: any): void {
        if (!unitData) {
            console.warn(`No data for unit ${this.unitId}`);
            return; 
        }

        this.countText.text = unitData.count.toString();
        // this.drawBadgeBackground();

        if (unitData.count <= 0 && this._state !== UnitState.Dead) {
            this._state = UnitState.Dead;
            this.alpha = 0.5;
            this.eventMode = 'none';
            this.badgeContainer.visible = false;
            this.view.filters = [];
            return;
        }

        if (this._state === UnitState.Dead) return;
        this.updateHpBar(unitData);
    }

    public updateGridPos(q: number, r: number, x: number, y: number) {
        this.q = q;
        this.r = r;
        this.x = x;
        this.y = y;
        this.zIndex = y; 
    }


    // --------- ANIMATIONS --------------
    private triggerImpact() {
        this.resolveImpact?.();
        this.resolveImpact = null;
        this.impactPromise = null;
    }

    public waitForImpact(signal?: AbortSignal): Promise<void> {
        if (!this.impactPromise) {
            this.impactPromise = new Promise<void>((resolve) => {
                this.resolveImpact = resolve;
            });
        }

        if (signal) {
            signal.addEventListener("abort", () => {
                this.resolveImpact?.();
            }, { once: true });
        }

        return this.impactPromise;
    }

    public waitForIdle(signal?: AbortSignal): Promise<void> {
        return new Promise((resolve, reject) => {
            if (signal?.aborted) return reject(new Error("Aborted"));

            const timer = gsap.delayedCall(0.05, resolve);

            signal?.addEventListener('abort', () => {
                timer.kill();
                reject(new Error("Aborted"));
            }, { once: true });
        });
    }
//#TODO: check if async required here
    public prepareForAction(): Promise<void> {
        this.stopIdle();
        return this.resetPose();
    }

    public async finishAction(signal?: AbortSignal): Promise<void> {
        await this.resetPose(); // Повернулися на місце
        // this.startIdle();       // Запустили дихання
        return this.waitForIdle(signal); // Повернули проміс затримки для контролера
    }

    // === Idle / breathing (synonyms) ===
    public startIdle() {
        // clear previous
        gsap.killTweensOf([this.visual, this.badgeContainer, this.shadowView.scale, this.shadowView]);
        
        const tl = gsap.timeline({
            repeat: -1,
            yoyo: true,
            defaults: { ease: "sine.inOut", duration: IDLE_ANIMATION_DURATION }
        });

        // Обидві анімації стартують ОДНОЧАСНО в точці 0
        tl.to(this.visual, { y: -18 }, 0);
        tl.to(this.badgeContainer, { y: "-=6" }, 0);
        
        // Анімація тіні: стає меншою і блідішою, коли юніт у повітрі
        tl.to(this.shadowView.scale, { x: 0.8, y: 0.8 }, 0);
        tl.to(this.shadowView, { alpha: 0.2 }, 0);
    }

    public stopIdle() {
        this.resetPose();
        gsap.killTweensOf([this.visual, this.badgeContainer, this.shadowView.scale, this.shadowView]);
        this.visual.y = 0;
        this.badgeContainer.y = this.config.badge?.y || -200;
        
        this.shadowView.scale.set(1);
        this.shadowView.alpha = 0.4;
    }

    // aliases
    public startBadgeHighlight() { 
        // gsap.to(this.badgeContainer, {
        //     pixi: {
        //         scale: 1.1,      // Можна анімувати разом із масштабом
        //         alpha: 1,
        //     },
        //     duration: 0.2
        // });
        this.hpBar.scale = 1.1;
        this.countText.tint = 0xe0b85a;
        this.badgeContainer.scale = 1.1;
        this.badgeContainer.alpha = 1;
     }
    public stopBadgeHighlight() { 
        
        // await gsap.to(this.badgeContainer, {
        //     pixi: {
        //         scale: 1.0,      // Можна анімувати разом із масштабом
        //         alpha: 0.75
        //     },
        //     duration: 0.2
        // });
        this.hpBar.scale = 1;
        this.countText.tint = 0xffffff;
        this.badgeContainer.scale = 1.0;
        this.badgeContainer.alpha = 0.75;
     }

    // === Animation API (promises) ===

    // Helper: normalize vector
    private norm(dx: number, dy: number) {
        const d = Math.hypot(dx, dy) || 1;
        return { x: dx / d, y: dy / d, dist: d };
    }

    public async animateMovement(
        splinePoints: HexPoint[],
    ): Promise<void> {
        return new Promise(resolve => {
            gsap.to(this, {
                duration: splinePoints.length * MOVEMENT_ANIMATION_DURATION_MULTIPLIER,
                ease: "power1.inOut",
                motionPath: { path: splinePoints, autoRotate: false, curviness: 0, type: "linear", },
                onUpdate: () => {
                    const newZ = Math.floor(this.y);
                    if (newZ !== this.zIndex) {
                        this.zIndex = newZ;
                    }
                },
                onComplete: resolve
            });
        });
    }

//TODO: no async?
    // Reset pose (do not start breathing automatically)
    public resetPose(durationMs = 220): Promise<void> {
        return new Promise((resolve) => {
            // Зупиняємо попередні твіни
            gsap.killTweensOf([this.visual, this.visual.scale, this.view]);

            // Перевірка на "вже на місці" для економії ресурсів
            if (Math.abs(this.visual.x) < 0.01 && this.visual.rotation === 0) {
                this.visual.x = 0;
                this.visual.y = 0;
                this.rotation = 0;
                return resolve();
            }

            gsap.to(this.visual, {
                x: 0,
                y: 0,
                rotation: 0,
                duration: durationMs / 1000,
                ease: "power2.out",
                onComplete: resolve,
                onInterrupt: resolve, // Важливо: резолвимо навіть якщо перервали
                // onOverwrite: resolve  // Додатковий захист GSAP
            });

            // Скейл зазвичай анімуємо окремо для плавності
            gsap.to(this.visual.scale, {
                x: 1,
                y: 1,
                duration: durationMs / 1000,
                ease: "power2.out"
            });
            gsap.to(this.shadowView.scale, {
                x: 1,
                y: 1,
                duration: durationMs / 1000,
                ease: "power2.out"
            });
            gsap.to(this.shadowView, {
                alpha: 0.6,
                duration: durationMs / 1000,
                ease: "power2.out"
            });
        });
    }
    

    // Attacker: play wind-up + strike (visual only). resolves when strike finishes (we keep visual offset until resetPose)
    public playAttackTowards(target: UnitStack): Promise<void> {
        return new Promise((resolve) => {
            gsap.killTweensOf([this.visual, this.visual.scale, this.view]);
            this.stopIdle();

            // готуємо impact promise ДО запуску таймлайна
            this.impactPromise = new Promise<void>((res) => {
                this.resolveImpact = res;
            });

            const dx = target.x - this.x;
            const dy = target.y - this.y;
            const len = Math.hypot(dx, dy) || 1;
            const nx = dx / len;

            const forward = this.FORWARD_PX ?? 14;

            const tl = gsap.timeline();

            // tl.to(this.visual, {
            //     pixi: { tint: '#ff3333' }, // потребує PixiPlugin для GSAP
            //     duration: 0.1,
            //     yoyo: true,
            //     repeat: 1,
            //     onComplete: () => {
            //         this.visual.tint = 0xffffff; // Скидаємо до нормального
            //     }
            // });

            // --- windup ---
            tl.to(this.visual, {
                x: `-=${nx * 6}`,
                y: 2,
                duration: (this.WINDUP_MS ?? 120) / 1000,
                ease: "power3.in"
            });

            // --- STRIKE (перед самим impact) ---
            tl.to(this.visual, {
                x: `+=${nx * forward}`,
                y: -6,
                duration: (this.STRIKE_MS ?? 80) / 1000,
                ease: "expo.out"
            });

            // 💥 ОСЬ ТУТ ФІЗИЧНИЙ УДАР
            tl.call(() => {
                this.triggerImpact();   // <<< ТОЧНИЙ КАДР IMPACT
            });

            // --- recover ---

            tl.to(this.visual, {
                x: 0,
                y: 0,
                duration: 0.16,
                ease: "elastic.out(1, 0.5)",
                onComplete: () => {
                    this.resetPose(120).then(resolve);
                }
            });

        });
    }


    private flashRed() {
        const filter = new PIXI.ColorMatrixFilter();
        this.visual.filters = [filter];

        // Матриця для червоного спалаху:
        // Ми обнуляємо зелений та синій канали, а червоний викручуємо
        filter.matrix = [
            0, 0, 0, 0, 0.5, // Red: беремо червоний + додаємо 0.5 яскравості
            0, 0, 0, 0, 0,   // Green: множимо на 0
            0, 0, 0, 0, 0,   // Blue: множимо на 0
            0, 0, 0, 0.5, 0    // Alpha: залишаємо як є
        ];

        // Повертаємо до норми через 100мс
        setTimeout(() => {
            this.visual.filters = [];
        }, 50);
    }

    public playHitReaction(attacker: UnitStack): Promise<void> {
        return new Promise(async (resolve) => {
            // kill tweens, зупинити idle
            gsap.killTweensOf([this.visual, this.visual.scale, this.view]);
            this.stopIdle();
            
            // напрям knockback відносно атакера
            const dx = (this.x ?? 0) - (attacker.x ?? 0);
            const dy = (this.y ?? 0) - (attacker.y ?? 0);
            const len = Math.hypot(dx, dy) || 1;
            const nx = dx / len;
            // const knock = isCrit ? this.KNOCKBACK_PX * this.CRIT_KNOCKBACK_MULT : this.KNOCKBACK_PX;
            const knock = this.KNOCKBACK_PX * 1.6;
            const impactUp = -4;
            const squashX = 0.92;
            const squashY = 1.06;

            const tl = gsap.timeline();

            

            // instant knockback + slight lift + squash
            tl.to(this.visual, {
                x: `+=${nx * knock}`,
                y: `+=${impactUp}`,
                duration: 0.06,
                ease: "power1.out"
            }, 0);

            gsap.to(this.visual, {
                pixi: { tint: '#ff3333' },
                duration: 0.06,
                yoyo: true,
                repeat: 1,
                onComplete: () => {
                    this.visual.tint = 0xffffff; // Скидаємо до нормального
                }
            });

            tl.to(this.badgeContainer, {
                pixi: { tint: '#ff3333' }, 
                duration: 0.1,
                yoyo: true,
                repeat: 1,
                onComplete: () => {
                    this.visual.tint = 0xffffff; // Скидаємо до нормального
                }
            }, 0);

            tl.to(this.visual.scale, {
                x: squashX,
                y: squashY,
                duration: 0.06,
                ease: "power1.out"
            }, 0);

            // recover - повернення в stance
            tl.to(this.visual, {
                x: 0,          // повернення у відносну базу
                y: 0,
                duration: (this.RECOVER_MS ?? 300) / 1000,
                ease: "elastic.out(1, 0.5)"
            }, "+=0.02");

            tl.to(this.visual.scale, {
                x: 1,
                y: 1,
                duration: (this.RECOVER_MS ?? 300) / 1000,
                ease: "elastic.out(1, 0.5)"
            }, "<");

            tl.to(this.visual, {
                x: 0,
                y: 0,
                duration: 0.16,
                ease: "elastic.out(1, 0.5)",
                onComplete: () => {
                    this.resetPose(120).then(resolve);
                }
            });
        });
    }

    public playCritReaction(attacker: UnitStack): Promise<void> {
        return new Promise(async (resolve) => {
            gsap.killTweensOf([this.visual, this.visual.scale, this.view]);
            this.stopIdle();
            
            const dx = (this.x ?? 0) - (attacker.x ?? 0);
            const dy = (this.y ?? 0) - (attacker.y ?? 0);
            const len = Math.hypot(dx, dy) || 1;
            const nx = dx / len;

            const knockback = 18; // значний відрив
            const lift = -10;
            const fall = 16;
            const stunTime = 0.02;

            const tl = gsap.timeline();

            // // 1) сильний stretch / squash
            // tl.to(this.visual.scale, {
            // x: 1.25,
            // y: 0.75,
            // duration: 0.06,
            // ease: "power2.out"
            // }, 0);

            // 2) sharp lift + knockback
            tl.to(this.visual, {
            x: `+=${nx * knockback}`,
            y: `+=${lift}`,
            duration: 0.09,
            ease: "power3.out"
            }, 0);

            gsap.to(this.visual, {
                pixi: { tint: '#ff3333' },
                duration: stunTime,
                yoyo: true,
                repeat: 1,
                onComplete: () => {
                    this.visual.tint = 0xffffff; // Скидаємо до нормального
                }
            });

            // 3) короткий stun
            tl.to({}, { duration: stunTime });

            // 4) fall down
            tl.to(this.visual, {
            y: `+=${fall}`,
            duration: 0.12,
            ease: "power2.in"
            });

            // // 5) collapse squash
            // tl.to(this.visual.scale, {
            // x: 0.85,
            // y: 1.2,
            // duration: 0.08,
            // ease: "power2.in"
            // }, "<");

            // 6) recovery back to neutral
            tl.to(this.visual.scale, {
            x: 1,
            y: 1,
            duration: 0.22,
            ease: "elastic.out(1, 0.5)"
            });

            tl.to(this.visual, {
            x: 0,
            y: 0,
            duration: 0.22,
            ease: "elastic.out(1, 0.5)"
            }, "<");

            // натяк: тут можна викликати particles / camera shake в момент падіння
            // tl.call(() => { this.emitCritParticles(); battleCamera.shake(6, 0.12); }, null, 0.09);

            tl.to(this.visual, {
                x: 0,
                y: 0,
                duration: 0.16,
                ease: "elastic.out(1, 0.5)",
                onComplete: () => {
                    this.resetPose(120).then(resolve);
                }
            });
        });
    }

    public playMissReaction(attacker: UnitStack): Promise<void> {
        return new Promise(async (resolve) => {
            gsap.killTweensOf([this.visual, this.visual.scale, this.view]);
            this.stopIdle();

            const dx = (this.x ?? 0) - (attacker.x ?? 0);
            const dy = (this.y ?? 0) - (attacker.y ?? 0);
            const len = Math.hypot(dx, dy) || 1;
            const nx = dx / len;

            const tl = gsap.timeline();

            // легке відхилення та ротація (subtle)
            tl.to(this.visual, {
            x: `+=${nx * 10}`,
            rotation: nx > 0 ? 0.06 : -0.06,
            duration: 0.1,
            ease: "power2.out"
            });

            // повернення в stance
            tl.to(this.visual, {
            x: 0,
            rotation: 0,
            duration: 0.22,
            ease: "back.out(2)"
            });

            tl.to(this.visual, {
                x: 0,
                y: 0,
                duration: 0.16,
                ease: "elastic.out(1, 0.5)",
                onComplete: () => {
                    this.resetPose(120).then(resolve);
                }
            });
        });
    }


    // Death animation (fall and fade)
    public playDeath(): Promise<void> {
        return new Promise((resolve) => {
            gsap.killTweensOf([this.visual, this.shadowView]);
            const tl = gsap.timeline({ onComplete: () => {
                this.shadowView.visible = false; 
                resolve() }
            });
            
            tl.to([this.visual], { y: 20, alpha: 0, duration: 0.45, ease: "power2.in" });
        });
    }

    public destroy(options?: PIXI.DestroyOptions): void {
        if (this.unsubStore) this.unsubStore();
        gsap.killTweensOf(this.visual);
        gsap.killTweensOf(this.view);
        super.destroy(options);
    }
}
